/** Zoom-based H3 parent rollup shared by the public CA map and GridPlan combined map. */

import { cellToParent, getResolution } from 'h3-js';

export const NATIVE_HEX_RES = 7;
export const HEX_RES_MIN = 4;

const SUM_KEYS = [
    'annual_energy_kwh',
    'hd_annual_energy_kwh',
    'transit_annual_energy_kwh',
    'week_kwh',
    'n_vehicles',
    'n_events',
    'display_energy_kwh',
];

const MAX_KEYS = [
    'peak_power_kw',
    'hd_peak_power_kw',
    'transit_peak_power_kw',
];

export function resolutionForZoom(zoom) {
    const z = Number(zoom);
    if (!Number.isFinite(z) || z < 7) return HEX_RES_MIN;
    if (z < 9) return 5;
    if (z < 10) return 6;
    return NATIVE_HEX_RES;
}

export function safeResolution(h3Index) {
    try {
        return getResolution(String(h3Index));
    } catch (_) {
        return NATIVE_HEX_RES;
    }
}

function addSum(target, source, key) {
    if (source[key] == null) return;
    const n = Number(source[key]);
    if (!Number.isFinite(n)) return;
    target[key] = (Number(target[key]) || 0) + n;
}

function raiseMax(target, source, key) {
    if (source[key] == null) return;
    const n = Number(source[key]);
    if (!Number.isFinite(n)) return;
    const current = Number(target[key]);
    target[key] = Number.isFinite(current) ? Math.max(current, n) : n;
}

export function aggregateHexes(hexes, targetRes) {
    if (!Array.isArray(hexes) || hexes.length === 0) return [];
    const res = Number(targetRes);
    if (!Number.isFinite(res) || res >= NATIVE_HEX_RES) return hexes;
    const byParent = new Map();
    for (const hex of hexes) {
        let parent;
        try {
            parent = cellToParent(String(hex.h3_index), res);
        } catch (_) {
            continue;
        }
        const existing = byParent.get(parent);
        if (!existing) {
            const rec = {
                h3_index: parent,
                child_count: 1,
                lat: hex.lat,
                lng: hex.lng,
            };
            for (const key of SUM_KEYS) addSum(rec, hex, key);
            for (const key of MAX_KEYS) raiseMax(rec, hex, key);
            byParent.set(parent, rec);
        } else {
            existing.child_count += 1;
            for (const key of SUM_KEYS) addSum(existing, hex, key);
            for (const key of MAX_KEYS) raiseMax(existing, hex, key);
        }
    }
    return Array.from(byParent.values()).map((rec) => {
        const hd = Number(rec.hd_annual_energy_kwh) || 0;
        const transit = Number(rec.transit_annual_energy_kwh) || 0;
        if (rec.hd_annual_energy_kwh != null || rec.transit_annual_energy_kwh != null) {
            rec.annual_energy_kwh = hd + transit;
        }
        return rec;
    });
}

export function selectionForDisplay(selectedHex, displayedHexes, displayRes) {
    if (!selectedHex?.h3_index) return null;
    const selectedRes = safeResolution(selectedHex.h3_index);
    if (selectedRes === displayRes) {
        return displayedHexes.find((h) => h.h3_index === selectedHex.h3_index) || selectedHex;
    }
    if (selectedRes > displayRes) {
        try {
            const parent = cellToParent(String(selectedHex.h3_index), displayRes);
            return displayedHexes.find((h) => h.h3_index === parent) || { ...selectedHex, h3_index: parent };
        } catch (_) {
            return selectedHex;
        }
    }
    return null;
}
